// to easily copy and paste components
import {useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Select from 'react-select';
import {useDispatch, useSelector} from 'react-redux';
import {Modal, Form} from 'react-bootstrap';
import {NotificationManager} from 'react-notifications';
import {BllinkLogger} from "../../../common/utils/bllink_loggers";
import { constants } from "../../../common/constants/constants";
import {hideLoader, showLoader} from '../../../store/common/slice';
import {getSelectMonthForDropdown} from '../../../common/shared/statelessMethods';
import BllinkUtils from '../../../common/utils/utils';
import PrintPdfIcons from '../../../common/export_documents/PrintPdfIcons';
import {StyledExpensesList} from './monthlyExpenses.styled';
import {selectCustomStyles} from '../../../common/shared/select.styled';
import {StyledModal} from '../../../common/shared/modal.styled';
import Table from '../../../common/table/TableFunctional';
import Search from '../../../images/search.svg';
import Bin from '../../../images/bin.svg';
import {GAEvent} from '../../../common/google_analytics/google_analytics';
import {useAccessToken} from '../../../common/hooks/useAccessToken';
import {StyledActionsBar} from '../report.styled';
import useBodyClass from '../../../common/hooks/useBodyClassName';
import {getBuildingTitle} from '../../../store/buildings/actions';
import { isAdminSelector } from '../../../store/auth/selectors';

const BllinkTime = BllinkUtils.BllinkTime;
const StringUtils = BllinkUtils.Strings;

export default function MonthlyExpensesTable({match, history}) {
    const {t} = useTranslation();
    const pdfContainer = useRef();
    const {buildingID, year, month} = match.params;
    const dispatch = useDispatch();
    const client = useAccessToken();
    const titleData = useSelector((state) => state.buildings.titleData);
    const ltr = useSelector((state) => state.common.ltrEnabled);
    const isAdmin = useSelector(isAdminSelector);

    const [expenses, setExpenses] = useState({
        rows: [],
        totalAmount: {}
    });
    const [monthData, setMonthData] = useState({
        monthsTexts: [],
        selectedIndex: 0
    });
    const [filters, setFilters] = useState({});
    const [confirmModalShow, setConfirmModalShow] = useState(false);
    const [rowToDelete, setRowToDelete] = useState({});
    const [deletePending, setDeletePending] = useState('one');

    document.title = t('titles.monthly_expense_table');
    useBodyClass('report');

    const onDeleteClick = row => {
        setRowToDelete(row.original);
        setConfirmModalShow(true);
    }

    const columns = useMemo(
        () => [
            {
                Header: t('reports.date_of_expense'),
                accessor: 'chargeDate',
                Cell: props => (<span>{BllinkTime.parseDate(props.value)}</span>),
                minWidth: 150
            },
            {
                Header: t('reports.total_to_pay'),
                accessor: 'amount',
                Cell: props => (<span>{StringUtils.numberWithCommas(props.value)}</span>),
                minWidth: 90
            },
            {
                Header: t('reports.vendor_name'),
                accessor: 'vendorName',
            },
            {
                Header: t('expenses.expense_record_table_type'),
                accessor: 'expenseTypeText',
            },
            {
                Header: t('payments.confirmation_id'),
                accessor: 'expenseID',
            },
            {
                Header: t('reports.expense_frequency'),
                accessor: 'frequency',
            },
            {
                Header: t('payments.payment_method'),
                accessor: 'methodText',
            },
            {
                Header: t('payments.comment'),
                accessor: 'comment',
                className: 'description',
                minWidth: 150
            },
            {
                Header: t('reports.actions'),
                accessor: 'isEditable',
                Cell: ({row, value}) => {
                    return value && (
                        <button
                            className='delete-btn'
                            onClick={() => onDeleteClick(row)}
                        >
                            <img src={Bin} alt="bin" />
                        </button>
                    );
                },
                className: 'with-button',
            },
        ],
        [expenses]
    );

    useEffect(() => {
        (async function () {
            if (!client) return;

            dispatch(showLoader());

            if (!titleData.translationKey) dispatch(getBuildingTitle({buildingID}));

            const monthsTexts = getSelectMonthForDropdown(year, t);
            setMonthData({
                monthsTexts,
                selectedIndex: monthsTexts.findIndex(item => item.value === `${parseInt(month)}_${year}`)
            });
            const data = await loadExpenses(buildingID, month, year);
            setExpenses({
                rows: data.Expenses,
                totalAmount: data.Total
            });
            dispatch(hideLoader());
        })();
    }, [client]);

    const loadExpenses = async (buildingId, month, year) => {
        dispatch(showLoader());
        let response = await client.managers.expenses.getMonthlyExpenses(buildingId, year, month) ?? { Expenses: [], Total: 0 };
        BllinkLogger.info(`response manager monthly expenses`, response);

        // To make search works properly
        response.Expenses = response.Expenses.map(item => {
            return {
                ...item,
                methodText: t(`offline_payments.${constants.APIContract.paymentMethodsKeys[item.method]}`),
                expenseTypeText: t(`expenses.expenseTypes.${item.expenseTypeKey}`),
                frequency: item.isRecurring === 'yes' ? t('expenses.recurring') : t('expenses.one_time')
            }
        });

        dispatch(hideLoader());
        return response;
    };

    const onMonthAndYearChanged = async monthYearData => {
        let parts = monthYearData.value.split('_');
        let newMonth = parts[0];
        let newYear = parts[1];
        const selectedIndex = monthData.monthsTexts.findIndex(option => option.value === `${parseInt(newMonth)}_${newYear}`)
        setMonthData({
            ...monthData,
            selectedIndex
        });
        const newUrl = `/${constants.pages.monthlyExpensesUrl}/${buildingID}/${newMonth}/${newYear}`
        history.push({pathname: newUrl});
        BllinkLogger.info(`newMonthAndYearSelected ${monthYearData} newYear ${newYear} ${newMonth}`);
        const response = await loadExpenses(buildingID, newMonth, newYear);
        setExpenses({
            rows: response.Expenses,
            totalAmount: response.Total
        });
    };

    const deleteExpense = async() => {
        dispatch(showLoader());
        const deleteAllPending = deletePending === 'all';
        const response = await client.managers.expenses.delete(rowToDelete.paymentID, deleteAllPending);
        BllinkLogger.info(`response delete expense`, response);

        const data = await loadExpenses(buildingID, month, year);
        setExpenses({
            rows: data.Expenses,
            totalAmount: data.Total
        });

        GAEvent(constants.GoogleAnalytics.Events.editTenants, 'deleteExpenseEvent', { deleteAllPending }, 'deleteExpenseStart');
        if (response.updated) {
            NotificationManager.success(t('reports.deleteExpenseSuccess'), '', 5000);
            GAEvent(constants.GoogleAnalytics.Events.deleteExpense, 'deleteExpenseEvent', { deleteAllPending }, 'deleteExpenseSuccess');
            setConfirmModalShow(false);
        }

        dispatch(hideLoader());
    }

    const handleRadioChange = evt => {
        evt.persist();
        setDeletePending(evt.target.value);
    }

    const tableData = useMemo(() => expenses.rows, [expenses]);
    const titleText = t('expenses.expense_monthly_report', {
        amount: BllinkUtils.Strings.numberWithCommas(expenses.totalAmount.value),
        currency: expenses.totalAmount.currency}
    );

    if (!isAdmin) return <div>{t('searchPayments.noAdminRights')}</div>;

    return (
        <StyledExpensesList>
            <h1 className='page-title'>
                {titleData.translationKey && (
                    <span className='building-title'>{t('payments.' + titleData.translationKey, titleData)}</span>
                )}
            </h1>
            <StyledActionsBar className={'toolbar align-items-center'}>
                <div className="control-with-icon search actions-bar-search">
                    <input
                        type="text"
                        className="form-control bllink-input-border"
                        name="search"
                        onChange={e => setFilters({
                            global: e.target.value
                        })}
                    />
                    <img src={Search} alt={t('search.search')} />
                </div>

                <div className='actions-bar-select wide'>
                    <Select
                        styles={selectCustomStyles}
                        options={monthData.monthsTexts}
                        value={monthData.monthsTexts[monthData.selectedIndex]}
                        onChange={onMonthAndYearChanged}
                    />
                </div>

                {
                    tableData && tableData.length > 0 && (
                        <PrintPdfIcons
                            classes={ltr ? "ml-auto" : "mr-auto"}
                            buildingID={buildingID}
                            eventLabel="tenantsList"
                            titleString={titleText}
                            excelTitle={titleText}
                            reportName={t('reports.expenses_report_title')}
                            pdfContainer={pdfContainer.current}
                        />
                    )
                }
            </StyledActionsBar>

            <div id="section-to-print" ref={pdfContainer}>
                {
                    tableData && tableData.length > 0 ? (
                        <Table
                            buildingId={buildingID}
                            columns={columns}
                            data={tableData}
                            filters={filters}
                            tableTitle={titleText}
                        />
                    ) : (<h2 className='text-center'>{t('reports.empty_table')}</h2>)
                }
            </div>

            <StyledModal
                centered
                show={confirmModalShow}
                onHide={() => setConfirmModalShow(false)}
            >
                <Modal.Header>
                    <Modal.Title>{t('reports.deleteExpenseConfirmText')}</Modal.Title>
                </Modal.Header>

                {
                    rowToDelete.isRecurring === 'yes' && (
                        <Modal.Body>
                            <Form>
                                <Form.Check
                                    id="one"
                                    type="radio"
                                    label={t('reports.deleteOneMonthExpense')}
                                    value="one"
                                    checked={deletePending === 'one'}
                                    onChange={handleRadioChange}
                                />
                                <Form.Check
                                    id="all"
                                    type="radio"
                                    label={t('reports.deleteExpenseRecurring')}
                                    value="all"
                                    checked={deletePending === 'all'}
                                    onChange={handleRadioChange}
                                />
                            </Form>
                        </Modal.Body>
                    )
                }

                <Modal.Footer>
                    <button
                        className={'bllink-button'}
                        onClick={() => setConfirmModalShow(false)}
                    >
                        {t('reports.cancel')}
                    </button>
                    <button
                        className={'bllink-button'}
                        onClick={deleteExpense}
                    >
                        {t('reports.deleteExpense')}
                    </button>
                </Modal.Footer>
            </StyledModal>
        </StyledExpensesList>
    );
}
